import { LogOut, MonitorUp, PanelTopOpen, SlidersHorizontal } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brand } from '../../components/Brand';
import { AccountDialog } from '../account/AccountDialog';
import { logout } from '../auth/authApi';
import { useAuth } from '../auth/authContext';
import { closeSocket } from '../../lib/socket';
import { appPath } from '../../lib/navigation';
import { NotificationsPopover } from './NotificationsPopover';
import { OnboardingDialog } from './OnboardingDialog';
import { DataArchive } from './DataArchive';
import { EventsManager } from './EventsManager';
import { SetupManager } from './SetupManager';
import { StatsDashboard } from './StatsDashboard';
import { TeamStatus } from './TeamStatus';
import { ModerationConsole } from './ModerationConsole';
import { AdminConsole } from './AdminConsole';
import { isModuleKey, shellModules, type ModuleKey } from './modules';
import { useShellData } from './useShellData';

export function HomeShell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [selected, setSelected] = useState<ModuleKey | null>(() => {
    const hash = window.location.hash.slice(1);
    return isModuleKey(hash) ? hash : null;
  });

  if (!user) throw new Error('HomeShell requires an authenticated user');

  const allowed = useMemo(
    () => shellModules.filter((module) => user.caps[module.capability]),
    [user.caps],
  );
  const shellData = useShellData(user.caps);
  const fallback = allowed.some((module) => module.key === 'mod') ? 'mod' : allowed[0]?.key ?? null;
  const activeKey = selected && allowed.some((module) => module.key === selected) ? selected : fallback;
  const activeModule = allowed.find((module) => module.key === activeKey) ?? null;
  const showOnboarding = !onboardingDismissed
    && allowed.length > 0
    && !user.caps.admin
    && (user.caps.events || (user.org && shellData.teamRooms.length > 0));
  const initials = userInitials(user.name || user.username);

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash.slice(1);
      if (isModuleKey(hash)) setSelected(hash);
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  useEffect(() => {
    if (activeKey && window.location.hash.slice(1) !== activeKey) {
      window.history.replaceState(null, '', `#${activeKey}`);
    }
  }, [activeKey]);

  function selectModule(key: ModuleKey) {
    if (!allowed.some((module) => module.key === key)) return;
    setSelected(key);
    window.history.replaceState(null, '', `#${key}`);
  }

  function finishOnboarding(key: ModuleKey) {
    setOnboardingDismissed(true);
    selectModule(key);
  }

  async function signOut() {
    try { await logout(); } catch { /* A local cookie clear is still followed by a session refresh. */ }
    closeSocket();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <Brand compact />

        <nav className="module-tabs" aria-label="Operator modules">
          {allowed.map((module) => {
            const Icon = module.icon;
            const badge = shellData.badges[module.key as keyof typeof shellData.badges] ?? 0;
            return (
              <button className={activeKey === module.key ? 'active' : ''} type="button" key={module.key} onClick={() => selectModule(module.key)} title={module.name}>
                <Icon size={15} />
                <span>{module.shortName}</span>
                {badge > 0 ? <span className="module-badge">{badge > 99 ? '99+' : badge}</span> : null}
              </button>
            );
          })}
        </nav>

        <div className="topbar-actions">
          {user.caps.av ? <button className="pill-button output-button" type="button" onClick={() => window.open(appPath('/av'), 'modiq_av')} title="Open AV Control"><SlidersHorizontal size={16} /><span>AV Control</span></button> : null}
          <div className={`live-pill ${shellData.live ? '' : 'off'}`}><span className="live-dot" /><span>LIVE</span></div>
          {user.caps.events || (user.org && shellData.teamRooms.length > 0) ? <button className="icon-button topbar-icon" type="button" onClick={() => setOnboardingDismissed(false)} aria-label="Choose rooms" title="Choose rooms"><PanelTopOpen size={18} /></button> : null}
          {user.caps.display ? <button className="pill-button output-button" type="button" onClick={() => window.open(appPath('/display'), 'modiq_tv')} title="Open TV"><MonitorUp size={16} /><span>Open TV</span></button> : null}
          <NotificationsPopover user={user} />
          <button className="user-chip" type="button" onClick={() => setAccountOpen(true)} title="Account settings">
            <span className="avatar">{initials}</span>
            <span className="user-info"><strong>{user.name || user.username}</strong><small>{user.role}</small></span>
          </button>
          <button className="icon-button topbar-icon" type="button" onClick={signOut} aria-label="Log out" title="Log out"><LogOut size={18} /></button>
        </div>
      </header>

      <section className="module-content">
        {activeModule ? (
          <ModuleView moduleKey={activeModule.key} user={user} />
        ) : (
          <div className="shell-empty">
            <SlidersHorizontal size={30} />
            <h1>Your account has no modules yet</h1>
            <p>{user.caps.av || user.caps.display ? 'Open your output tools from the controls above.' : 'An admin needs to grant you access. Your tools will appear here.'}</p>
          </div>
        )}
      </section>

      {accountOpen ? <AccountDialog open onClose={() => setAccountOpen(false)} /> : null}
      {showOnboarding ? <OnboardingDialog user={user} teamRooms={shellData.teamRooms} onSelect={finishOnboarding} onClose={() => setOnboardingDismissed(true)} /> : null}
    </main>
  );
}

function ModuleView({ moduleKey, user }: { moduleKey: ModuleKey; user: NonNullable<ReturnType<typeof useAuth>['user']> }) {
  switch (moduleKey) {
    case 'data': return <DataArchive />;
    case 'setup': return <SetupManager />;
    case 'events': return <EventsManager />;
    case 'stats': return <StatsDashboard isAdmin={user.caps.admin} />;
    case 'team': return <TeamStatus currentUserId={user.id} />;
    case 'mod': return <ModerationConsole user={user} />;
    case 'admin': return <AdminConsole user={user} />;
  }
}

function userInitials(displayName: string): string {
  const parts = displayName.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return `${parts[0]?.charAt(0) || '?'}${parts[1]?.charAt(0) || ''}`.toUpperCase();
}
